// Dynamic health tips generator for travel destinations

interface VaccineInfo {
  name: string;
  type: 'mandatory' | 'recommended' | 'routine';
  timing: string;
  description?: string;
}

interface DestinationInfo {
  name: string;
  vaccinations: VaccineInfo[];
  healthRisks: string[];
  pharmacyItems: string[];
  nutritionTips: string[];
  currentAlerts: any[];
}

// Health risk profiles for different countries/regions
const countryHealthProfiles: Record<string, any> = {
  // Southeast Asia
  'Thailand': {
    climate: 'tropical',
    diseases: ['dengue', 'malaria', 'japanese_encephalitis', 'chikungunya'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'rural_areas'
  },
  'Vietnam': {
    climate: 'tropical',
    diseases: ['dengue', 'malaria', 'japanese_encephalitis', 'typhoid'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'high_concern',
    yellowFeverRequired: false,
    malariaRisk: 'rural_areas'
  },
  'Malaysia': {
    climate: 'tropical',
    diseases: ['dengue', 'malaria', 'typhoid', 'chikungunya'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'borneo_areas'
  },
  'Indonesia': {
    climate: 'tropical',
    diseases: ['dengue', 'malaria', 'typhoid', 'japanese_encephalitis'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'high_concern',
    yellowFeverRequired: false,
    malariaRisk: 'eastern_islands'
  },
  'Singapore': {
    climate: 'tropical',
    diseases: ['dengue', 'chikungunya'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Philippines': {
    climate: 'tropical',
    diseases: ['dengue', 'malaria', 'typhoid', 'japanese_encephalitis'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'mindanao_palawan'
  },

  // East Asia
  'Japan': {
    climate: 'temperate',
    diseases: ['japanese_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'South Korea': {
    climate: 'temperate',
    diseases: ['japanese_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'China': {
    climate: 'varied',
    diseases: ['japanese_encephalitis', 'typhoid', 'hepatitis_a'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'very_high_concern',
    yellowFeverRequired: false,
    malariaRisk: 'southern_regions'
  },
  'Taiwan': {
    climate: 'subtropical',
    diseases: ['japanese_encephalitis', 'dengue'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Hong Kong': {
    climate: 'subtropical',
    diseases: ['dengue'],
    waterSafety: 'very_low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Macau': {
    climate: 'subtropical',
    diseases: ['dengue'],
    waterSafety: 'very_low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },

  // South Asia
  'India': {
    climate: 'tropical',
    diseases: ['dengue', 'malaria', 'typhoid', 'japanese_encephalitis', 'chikungunya'],
    waterSafety: 'very_high_risk',
    foodSafety: 'very_high_risk',
    airQuality: 'very_high_concern',
    yellowFeverRequired: false,
    malariaRisk: 'widespread'
  },
  'Nepal': {
    climate: 'varied',
    diseases: ['typhoid', 'japanese_encephalitis', 'altitude_sickness'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'high_concern',
    yellowFeverRequired: false,
    malariaRisk: 'terai_region'
  },
  'Sri Lanka': {
    climate: 'tropical',
    diseases: ['dengue', 'typhoid', 'chikungunya'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Bangladesh': {
    climate: 'tropical',
    diseases: ['dengue', 'malaria', 'typhoid', 'japanese_encephalitis'],
    waterSafety: 'very_high_risk',
    foodSafety: 'very_high_risk',
    airQuality: 'very_high_concern',
    yellowFeverRequired: false,
    malariaRisk: 'southeastern_regions'
  },
  'Pakistan': {
    climate: 'varied',
    diseases: ['dengue', 'malaria', 'typhoid'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'very_high_concern',
    yellowFeverRequired: false,
    malariaRisk: 'southern_regions'
  },

  // Middle East
  'United Arab Emirates': {
    climate: 'desert',
    diseases: ['heat_illness'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Saudi Arabia': {
    climate: 'desert',
    diseases: ['heat_illness', 'mers_cov'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'southwestern_regions',
    hajjUmrahRequirements: true
  },
  'Qatar': {
    climate: 'desert',
    diseases: ['heat_illness'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Turkey': {
    climate: 'temperate',
    diseases: ['hepatitis_a'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Israel': {
    climate: 'mediterranean',
    diseases: ['heat_illness'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Jordan': {
    climate: 'desert',
    diseases: ['heat_illness'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },

  // Africa
  'Kenya': {
    climate: 'tropical',
    diseases: ['malaria', 'dengue', 'yellow_fever', 'typhoid', 'meningitis'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: true,
    malariaRisk: 'widespread',
    altitudeSickness: 'nairobi_highlands'
  },
  'Tanzania': {
    climate: 'tropical',
    diseases: ['malaria', 'dengue', 'yellow_fever', 'typhoid', 'meningitis'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: true,
    malariaRisk: 'widespread',
    altitudeSickness: 'kilimanjaro'
  },
  'South Africa': {
    climate: 'varied',
    diseases: ['malaria', 'typhoid', 'hepatitis_a'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'high_concern',
    yellowFeverRequired: false,
    malariaRisk: 'kruger_region'
  },
  'Egypt': {
    climate: 'desert',
    diseases: ['hepatitis_a', 'typhoid'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'high_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Morocco': {
    climate: 'mediterranean',
    diseases: ['hepatitis_a', 'typhoid'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },

  // South America
  'Brazil': {
    climate: 'tropical',
    diseases: ['dengue', 'yellow_fever', 'zika', 'typhoid', 'hepatitis_a', 'malaria'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: true,
    malariaRisk: 'amazon_region'
  },
  'Argentina': {
    climate: 'varied',
    diseases: ['hepatitis_a', 'typhoid', 'altitude_sickness'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'northern_provinces'
  },
  'Chile': {
    climate: 'varied',
    diseases: ['hepatitis_a', 'altitude_sickness'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Peru': {
    climate: 'varied',
    diseases: ['yellow_fever', 'malaria', 'dengue', 'altitude_sickness', 'typhoid', 'hepatitis_a'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: true,
    malariaRisk: 'amazon_regions'
  },
  'Colombia': {
    climate: 'tropical',
    diseases: ['yellow_fever', 'dengue', 'zika', 'malaria', 'typhoid', 'hepatitis_a'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: true,
    malariaRisk: 'rural_areas'
  },
  'Ecuador': {
    climate: 'tropical',
    diseases: ['yellow_fever', 'dengue', 'malaria', 'altitude_sickness', 'typhoid'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: true,
    malariaRisk: 'coastal_amazon'
  },
  'Bolivia': {
    climate: 'varied',
    diseases: ['yellow_fever', 'dengue', 'altitude_sickness', 'typhoid', 'hepatitis_a'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: true,
    malariaRisk: 'lowland_areas'
  },
  'Venezuela': {
    climate: 'tropical',
    diseases: ['malaria', 'dengue', 'yellow_fever', 'typhoid', 'hepatitis_a'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: true,
    malariaRisk: 'widespread'
  },
  'Paraguay': {
    climate: 'subtropical',
    diseases: ['dengue', 'typhoid', 'hepatitis_a'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Uruguay': {
    climate: 'temperate',
    diseases: ['hepatitis_a'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Guyana': {
    climate: 'tropical',
    diseases: ['malaria', 'dengue', 'yellow_fever', 'typhoid'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: true,
    malariaRisk: 'widespread'
  },
  'Suriname': {
    climate: 'tropical',
    diseases: ['malaria', 'dengue', 'yellow_fever', 'typhoid'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: true,
    malariaRisk: 'interior_regions'
  },
  'French Guiana': {
    climate: 'tropical',
    diseases: ['yellow_fever', 'dengue', 'malaria', 'typhoid'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: true,
    malariaRisk: 'interior_regions'
  },

  // Central America
  'Mexico': {
    climate: 'varied',
    diseases: ['dengue', 'zika', 'typhoid', 'hepatitis_a'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'high_concern',
    yellowFeverRequired: false,
    malariaRisk: 'southern_states'
  },
  'Guatemala': {
    climate: 'tropical',
    diseases: ['dengue', 'zika', 'malaria', 'typhoid', 'hepatitis_a'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'rural_areas'
  },
  'Belize': {
    climate: 'tropical',
    diseases: ['dengue', 'zika', 'hepatitis_a'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Honduras': {
    climate: 'tropical',
    diseases: ['dengue', 'zika', 'malaria', 'typhoid'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'coastal_areas'
  },
  'El Salvador': {
    climate: 'tropical',
    diseases: ['dengue', 'zika', 'typhoid'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Nicaragua': {
    climate: 'tropical',
    diseases: ['dengue', 'zika', 'malaria', 'typhoid'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'rural_areas'
  },
  'Costa Rica': {
    climate: 'tropical',
    diseases: ['dengue', 'zika', 'hepatitis_a'],
    waterSafety: 'moderate_risk',
    foodSafety: 'low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'limited_areas'
  },
  'Panama': {
    climate: 'tropical',
    diseases: ['dengue', 'zika', 'yellow_fever', 'malaria', 'typhoid'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'darien_san_blas'
  },

  // Caribbean
  'Cuba': {
    climate: 'tropical',
    diseases: ['dengue', 'zika'],
    waterSafety: 'low_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Jamaica': {
    climate: 'tropical',
    diseases: ['dengue', 'zika'],
    waterSafety: 'low_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Dominican Republic': {
    climate: 'tropical',
    diseases: ['dengue', 'zika', 'malaria', 'typhoid'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'limited_areas'
  },
  'Puerto Rico': {
    climate: 'tropical',
    diseases: ['dengue', 'zika'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Haiti': {
    climate: 'tropical',
    diseases: ['malaria', 'dengue', 'zika', 'typhoid', 'hepatitis_a', 'cholera'],
    waterSafety: 'very_high_risk',
    foodSafety: 'very_high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'widespread'
  },
  'Trinidad and Tobago': {
    climate: 'tropical',
    diseases: ['dengue', 'zika'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: true,
    malariaRisk: 'none'
  },
  'Bahamas': {
    climate: 'tropical',
    diseases: ['dengue'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Barbados': {
    climate: 'tropical',
    diseases: ['dengue', 'zika'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },

  // Europe
  'United Kingdom': {
    climate: 'temperate',
    diseases: ['tick_borne_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'France': {
    climate: 'temperate',
    diseases: ['tick_borne_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Germany': {
    climate: 'temperate',
    diseases: ['tick_borne_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Italy': {
    climate: 'mediterranean',
    diseases: ['tick_borne_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Spain': {
    climate: 'mediterranean',
    diseases: ['tick_borne_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Netherlands': {
    climate: 'temperate',
    diseases: ['tick_borne_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Switzerland': {
    climate: 'alpine',
    diseases: ['tick_borne_encephalitis', 'altitude_sickness'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'very_low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Austria': {
    climate: 'alpine',
    diseases: ['tick_borne_encephalitis', 'altitude_sickness'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Greece': {
    climate: 'mediterranean',
    diseases: ['tick_borne_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },

  // Americas
  'United States': {
    climate: 'varied',
    diseases: ['tick_borne_illness', 'west_nile_virus'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Canada': {
    climate: 'temperate',
    diseases: ['tick_borne_illness'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },

  // Oceania
  'Australia': {
    climate: 'varied',
    diseases: ['dengue', 'japanese_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'New Zealand': {
    climate: 'temperate',
    diseases: [],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'very_low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Fiji': {
    climate: 'tropical',
    diseases: ['dengue', 'typhoid', 'zika'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },

  // Southern Africa
  'Zimbabwe': {
    climate: 'subtropical',
    diseases: ['malaria', 'typhoid', 'hepatitis_a', 'cholera', 'tuberculosis'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas', // Only required if coming from YF endemic countries
    malariaRisk: 'year_round_except_harare_bulawayo'
  },
  'Zambia': {
    climate: 'subtropical',
    diseases: ['malaria', 'typhoid', 'hepatitis_a', 'cholera', 'rabies', 'tuberculosis'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'year_round_all_areas'
  },
  'Botswana': {
    climate: 'semi_arid',
    diseases: ['malaria', 'typhoid', 'hepatitis_a', 'rabies'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'northern_okavango_chobe_areas'
  },
  'Namibia': {
    climate: 'desert',
    diseases: ['malaria', 'typhoid', 'hepatitis_a', 'rabies'],
    waterSafety: 'moderate_risk',
    foodSafety: 'low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'northern_caprivi_strip_kunene_kavango'
  },
  'Mozambique': {
    climate: 'tropical',
    diseases: ['malaria', 'typhoid', 'hepatitis_a', 'cholera', 'dengue', 'schistosomiasis'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'year_round_all_areas'
  },
  'Madagascar': {
    climate: 'tropical',
    diseases: ['malaria', 'typhoid', 'hepatitis_a', 'plague', 'schistosomiasis', 'rabies'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'year_round_except_antananarivo'
  },
  'Lesotho': {
    climate: 'temperate',
    diseases: ['typhoid', 'hepatitis_a', 'tuberculosis', 'hiv'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false, // No yellow fever risk or requirement
    malariaRisk: 'none'
  },
  'Eswatini': {
    climate: 'subtropical',
    diseases: ['malaria', 'typhoid', 'hepatitis_a', 'tuberculosis', 'hiv', 'rabies'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'eastern_lowlands_big_bend_mhlume'
  },

  // More Caribbean
  'Aruba': {
    climate: 'tropical',
    diseases: ['dengue', 'zika'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false, // No requirement
    malariaRisk: 'none'
  },
  'Saint Lucia': {
    climate: 'tropical',
    diseases: ['dengue', 'zika', 'chikungunya'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas', // Only if coming from endemic countries
    malariaRisk: 'none'
  },
  'Grenada': {
    climate: 'tropical',
    diseases: ['dengue', 'zika', 'chikungunya'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'none'
  },
  'Antigua and Barbuda': {
    climate: 'tropical',
    diseases: ['dengue', 'zika'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'none'
  },
  'Saint Kitts and Nevis': {
    climate: 'tropical',
    diseases: ['dengue', 'zika'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'none'
  },
  'Cayman Islands': {
    climate: 'tropical',
    diseases: ['dengue', 'zika'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Turks and Caicos': {
    climate: 'tropical',
    diseases: ['dengue', 'zika'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },

  // More Europe
  'Portugal': {
    climate: 'mediterranean',
    diseases: [],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Belgium': {
    climate: 'temperate',
    diseases: [],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Czech Republic': {
    climate: 'temperate',
    diseases: ['tick_borne_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Poland': {
    climate: 'temperate',
    diseases: ['tick_borne_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Hungary': {
    climate: 'temperate',
    diseases: ['tick_borne_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Croatia': {
    climate: 'mediterranean',
    diseases: ['tick_borne_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Romania': {
    climate: 'temperate',
    diseases: ['tick_borne_encephalitis', 'hepatitis_a'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Bulgaria': {
    climate: 'temperate',
    diseases: ['tick_borne_encephalitis', 'hepatitis_a'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Denmark': {
    climate: 'temperate',
    diseases: [],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Sweden': {
    climate: 'temperate',
    diseases: ['tick_borne_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Norway': {
    climate: 'temperate',
    diseases: ['tick_borne_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'very_low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Finland': {
    climate: 'temperate',
    diseases: ['tick_borne_encephalitis'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'very_low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Iceland': {
    climate: 'arctic',
    diseases: [],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'very_low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },

  // More Southeast Asia
  'Cambodia': {
    climate: 'tropical',
    diseases: ['dengue', 'malaria', 'typhoid', 'japanese_encephalitis', 'hepatitis_a', 'hepatitis_b'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'rural_forested_areas'
  },
  'Laos': {
    climate: 'tropical',
    diseases: ['dengue', 'malaria', 'typhoid', 'japanese_encephalitis', 'hepatitis_a', 'rabies'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'rural_areas_not_vientiane'
  },
  'Myanmar': {
    climate: 'tropical',
    diseases: ['dengue', 'malaria', 'typhoid', 'japanese_encephalitis', 'hepatitis_a', 'hepatitis_e', 'tuberculosis'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'high_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'rural_areas_especially_borders'
  },
  'Brunei': {
    climate: 'tropical',
    diseases: ['dengue', 'hepatitis_a'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'none'
  },

  // More Pacific
  'Samoa': {
    climate: 'tropical',
    diseases: ['dengue', 'zika', 'chikungunya', 'typhoid', 'hepatitis_a'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'none'
  },
  'Tonga': {
    climate: 'tropical',
    diseases: ['dengue', 'zika', 'chikungunya', 'typhoid'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'none'
  },
  'Vanuatu': {
    climate: 'tropical',
    diseases: ['malaria', 'dengue', 'zika', 'chikungunya', 'typhoid'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'some_islands_esp_rural'
  },
  'Papua New Guinea': {
    climate: 'tropical',
    diseases: ['malaria', 'dengue', 'typhoid', 'hepatitis_a', 'tuberculosis', 'japanese_encephalitis'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'year_round_all_areas'
  },
  'New Caledonia': {
    climate: 'tropical',
    diseases: ['dengue', 'zika', 'chikungunya'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'none'
  },
  'French Polynesia': {
    climate: 'tropical',
    diseases: ['dengue', 'zika', 'chikungunya'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'none'
  },
  'Cook Islands': {
    climate: 'tropical',
    diseases: ['dengue'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },

  // More Africa
  'Ethiopia': {
    climate: 'varied',
    diseases: ['malaria', 'typhoid', 'hepatitis_a', 'yellow_fever', 'meningitis', 'rabies'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'mandatory_for_entry', // Required for all travelers over 9 months
    malariaRisk: 'below_2500m_altitude'
  },
  'Ghana': {
    climate: 'tropical',
    diseases: ['malaria', 'yellow_fever', 'typhoid', 'hepatitis_a', 'meningitis', 'cholera', 'schistosomiasis'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'mandatory_for_entry', // Required for all travelers
    malariaRisk: 'year_round_all_areas'
  },
  'Nigeria': {
    climate: 'tropical',
    diseases: ['malaria', 'yellow_fever', 'typhoid', 'hepatitis_a', 'meningitis', 'cholera', 'lassa_fever'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'high_concern',
    yellowFeverRequired: 'mandatory_for_entry', // Required for all travelers
    malariaRisk: 'year_round_all_areas'
  },
  'Senegal': {
    climate: 'tropical',
    diseases: ['malaria', 'yellow_fever', 'typhoid', 'hepatitis_a', 'meningitis', 'schistosomiasis'],
    waterSafety: 'high_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'mandatory_for_entry', // Required for all travelers
    malariaRisk: 'year_round_all_areas'
  },
  'Uganda': {
    climate: 'tropical',
    diseases: ['malaria', 'yellow_fever', 'typhoid', 'hepatitis_a', 'cholera', 'meningitis', 'rabies'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'mandatory_for_entry', // Required for all travelers
    malariaRisk: 'year_round_all_areas'
  },
  'Rwanda': {
    climate: 'temperate',
    diseases: ['malaria', 'typhoid', 'hepatitis_a', 'cholera', 'rabies'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'low_altitude_areas_below_1900m'
  },
  'Mauritius': {
    climate: 'tropical',
    diseases: ['dengue', 'chikungunya', 'hepatitis_a'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'none'
  },
  'Seychelles': {
    climate: 'tropical',
    diseases: ['dengue', 'chikungunya'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'none'
  },
  'Cyprus': {
    climate: 'mediterranean',
    diseases: ['tick_borne_illness'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Malta': {
    climate: 'mediterranean',
    diseases: [],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Andorra': {
    climate: 'alpine',
    diseases: ['altitude_sickness'],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'very_low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Luxembourg': {
    climate: 'temperate',
    diseases: [],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Gibraltar': {
    climate: 'mediterranean',
    diseases: [],
    waterSafety: 'very_low_risk',
    foodSafety: 'very_low_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Kosovo': {
    climate: 'continental',
    diseases: ['tick_borne_encephalitis'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'North Macedonia': {
    climate: 'continental',
    diseases: ['tick_borne_encephalitis'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Ukraine': {
    climate: 'continental',
    diseases: ['tick_borne_encephalitis', 'hepatitis_a'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Russia': {
    climate: 'varied',
    diseases: ['tick_borne_encephalitis', 'hepatitis_a', 'diphtheria'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Bhutan': {
    climate: 'varied',
    diseases: ['altitude_sickness', 'hepatitis_a', 'typhoid', 'japanese_encephalitis'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'southern_border_areas'
  },
  'Afghanistan': {
    climate: 'arid',
    diseases: ['malaria', 'typhoid', 'hepatitis_a', 'hepatitis_e', 'leishmaniasis', 'tuberculosis'],
    waterSafety: 'very_high_risk',
    foodSafety: 'very_high_risk',
    airQuality: 'high_concern',
    yellowFeverRequired: false,
    malariaRisk: 'below_2000m_may_to_november'
  },
  'Yemen': {
    climate: 'desert',
    diseases: ['malaria', 'dengue', 'cholera', 'typhoid', 'hepatitis_a', 'meningitis'],
    waterSafety: 'very_high_risk',
    foodSafety: 'very_high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'mandatory_for_entry',
    malariaRisk: 'below_2000m_year_round'
  },
  'Somalia': {
    climate: 'tropical_arid',
    diseases: ['malaria', 'dengue', 'cholera', 'typhoid', 'hepatitis_a', 'tuberculosis', 'meningitis'],
    waterSafety: 'very_high_risk',
    foodSafety: 'very_high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'year_round_all_areas'
  },
  'Eritrea': {
    climate: 'desert',
    diseases: ['malaria', 'typhoid', 'hepatitis_a', 'tuberculosis', 'meningitis'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'mandatory_for_entry',
    malariaRisk: 'below_2200m_year_round'
  },
  'South Sudan': {
    climate: 'tropical',
    diseases: ['malaria', 'yellow_fever', 'cholera', 'typhoid', 'hepatitis_a', 'meningitis', 'sleeping_sickness'],
    waterSafety: 'very_high_risk',
    foodSafety: 'very_high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'mandatory_for_entry',
    malariaRisk: 'year_round_all_areas'
  },
  'Burundi': {
    climate: 'tropical',
    diseases: ['malaria', 'cholera', 'typhoid', 'hepatitis_a', 'meningitis', 'schistosomiasis'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'mandatory_for_entry',
    malariaRisk: 'year_round_all_areas'
  },
  'The Gambia': {
    climate: 'tropical',
    diseases: ['malaria', 'yellow_fever', 'typhoid', 'hepatitis_a', 'meningitis', 'schistosomiasis'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'mandatory_for_entry',
    malariaRisk: 'year_round_all_areas'
  },
  'Togo': {
    climate: 'tropical',
    diseases: ['malaria', 'yellow_fever', 'typhoid', 'hepatitis_a', 'meningitis', 'cholera', 'schistosomiasis'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'mandatory_for_entry',
    malariaRisk: 'year_round_all_areas'
  },
  'Benin': {
    climate: 'tropical',
    diseases: ['malaria', 'yellow_fever', 'typhoid', 'hepatitis_a', 'meningitis', 'cholera', 'schistosomiasis'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'mandatory_for_entry',
    malariaRisk: 'year_round_all_areas'
  },
  'Mauritania': {
    climate: 'desert',
    diseases: ['malaria', 'yellow_fever', 'typhoid', 'hepatitis_a', 'meningitis', 'schistosomiasis'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'mandatory_for_entry',
    malariaRisk: 'southern_regions_july_to_october'
  },
  'Western Sahara': {
    climate: 'desert',
    diseases: ['hepatitis_a', 'typhoid'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Angola': {
    climate: 'tropical',
    diseases: ['malaria', 'yellow_fever', 'typhoid', 'hepatitis_a', 'cholera', 'dengue', 'schistosomiasis', 'sleeping_sickness'],
    waterSafety: 'very_high_risk',
    foodSafety: 'very_high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'mandatory_for_entry',
    malariaRisk: 'year_round_all_areas'
  },
  'North Korea': {
    climate: 'continental',
    diseases: ['tuberculosis', 'hepatitis_b', 'typhoid', 'japanese_encephalitis'],
    waterSafety: 'high_risk',
    foodSafety: 'high_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: 'if_arriving_from_endemic_areas',
    malariaRisk: 'none'
  },
  'Tunisia': {
    climate: 'mediterranean',
    diseases: ['hepatitis_a', 'typhoid', 'rabies'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Algeria': {
    climate: 'desert',
    diseases: ['hepatitis_a', 'typhoid', 'rabies'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },

  // More Middle East
  'Oman': {
    climate: 'desert',
    diseases: ['dengue', 'mers'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Kuwait': {
    climate: 'desert',
    diseases: ['mers'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'high_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Bahrain': {
    climate: 'desert',
    diseases: ['mers'],
    waterSafety: 'low_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },
  'Lebanon': {
    climate: 'mediterranean',
    diseases: ['hepatitis_a', 'typhoid'],
    waterSafety: 'moderate_risk',
    foodSafety: 'low_risk',
    airQuality: 'moderate_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  },

  // More South America
  'Galapagos': {
    climate: 'tropical',
    diseases: ['dengue', 'zika'],
    waterSafety: 'moderate_risk',
    foodSafety: 'moderate_risk',
    airQuality: 'low_concern',
    yellowFeverRequired: false,
    malariaRisk: 'none'
  }
};

function extractCountryFromDestination(destination: string): string {
  // Fallback: extract country name from common patterns
  const parts = destination.split(',');
  if (parts.length >= 2) {
    const potentialCountry = parts[parts.length - 1].trim();
    
    // Check if it matches any of our known countries
    const countryKeys = Object.keys(countryHealthProfiles);
    const matchedCountry = countryKeys.find(country => 
      country.toLowerCase() === potentialCountry.toLowerCase()
    );
    
    return matchedCountry || potentialCountry;
  }
  
  // Check if the destination contains any known country names
  const countryKeys = Object.keys(countryHealthProfiles);
  const matchedCountry = countryKeys.find(country => 
    destination.toLowerCase().includes(country.toLowerCase())
  );
  
  return matchedCountry || destination;
}

// Use live vaccination data from Supabase Edge Function
async function generateVaccinations(profile: any, destination: string): Promise<VaccineInfo[]> {
  try {
    // Try to fetch live vaccination data
    const { supabase } = await import('@/integrations/supabase/client');
    
    const { data, error } = await supabase.functions.invoke('vaccination-requirements', {
      body: { destination }
    });

    if (!error && data?.vaccinations) {
      console.log('Using live vaccination data for:', destination);
      return data.vaccinations;
    } else {
      console.warn('Live vaccination data failed, using fallback for:', destination, error);
    }
  } catch (error) {
    console.warn('Error fetching live vaccination data:', error);
  }

  // Fallback to static data based on profile
  const vaccinations: VaccineInfo[] = [
    {
      name: 'Routine Vaccines (MMR, DPT, Flu, COVID-19)',
      type: 'routine',
      timing: '2-4 weeks before travel',
      description: 'Ensure all routine vaccinations are up to date'
    }
  ];

  // Yellow Fever
  if (profile.yellowFeverRequired) {
    vaccinations.unshift({
      name: 'Yellow Fever',
      type: 'mandatory',
      timing: '10+ days before travel (required for entry)',
      description: 'International certificate required for entry'
    });
  }
  // Conditional Yellow Fever requirement for Malaysia when arriving from risk areas or >12h transit
  const destLower = destination.toLowerCase();
  const hasYellowFever = vaccinations.some(v => v.name.toLowerCase().includes('yellow fever'));
  if (!hasYellowFever && (destLower.includes('malaysia') || destLower.includes('kuala lumpur'))) {
    vaccinations.unshift({
      name: 'Yellow Fever',
      type: 'mandatory',
      timing: '10+ days before travel',
      description: 'Required only if arriving from or transiting (>12h) through a Yellow Fever risk country; International Certificate required'
    });
  }

  // Hepatitis A
  if (['moderate_risk', 'high_risk', 'very_high_risk'].includes(profile.foodSafety)) {
    vaccinations.push({
      name: 'Hepatitis A',
      type: 'recommended',
      timing: '2-4 weeks before travel',
      description: 'Protection against liver infection from contaminated food/water'
    });
  }

  // Hepatitis B
  vaccinations.push({
    name: 'Hepatitis B',
    type: 'recommended',
    timing: '6-8 weeks before travel (3-dose series)',
    description: 'Long-term protection against bloodborne infection'
  });

  // Typhoid
  if (profile.diseases.includes('typhoid')) {
    vaccinations.push({
      name: 'Typhoid',
      type: 'recommended',
      timing: '2-4 weeks before travel',
      description: 'Protection against bacterial infection from contaminated food/water'
    });
  }

  // Japanese Encephalitis
  if (profile.diseases.includes('japanese_encephalitis')) {
    vaccinations.push({
      name: 'Japanese Encephalitis',
      type: 'recommended',
      timing: '6-8 weeks before travel (2-dose series)',
      description: 'For rural areas and long stays (>1 month)'
    });
  }

  // Malaria Prophylaxis
  if (profile.malariaRisk && profile.malariaRisk !== 'none') {
    vaccinations.push({
      name: 'Malaria Prophylaxis',
      type: profile.malariaRisk === 'widespread' ? 'mandatory' : 'recommended',
      timing: '1-2 weeks before travel (start earlier)',
      description: `For ${profile.malariaRisk} - consult travel medicine doctor`
    });
  }

  // Meningococcal Meningitis
  if (profile.diseases.includes('meningitis') || profile.hajjUmrahRequirements) {
    vaccinations.push({
      name: 'Meningococcal Meningitis (ACWY)',
      type: profile.hajjUmrahRequirements ? 'mandatory' : 'recommended',
      timing: '10+ days before travel',
      description: profile.hajjUmrahRequirements ? 
        'Required for Hajj/Umrah pilgrimage' : 
        'For meningitis belt regions and crowded conditions'
    });
  }

  // Tick-borne Encephalitis
  if (profile.diseases.includes('tick_borne_encephalitis')) {
    vaccinations.push({
      name: 'Tick-borne Encephalitis',
      type: 'recommended',
      timing: '6-12 weeks before travel (2-3 dose series)',
      description: 'For rural/forest areas in spring/summer (April-October)'
    });
  }

  return vaccinations;
}

function generateHealthRisks(profile: any, destination: string): string[] {
  const risks: string[] = [];

  // Disease-specific risks
  if (profile.diseases.includes('dengue')) risks.push('Dengue fever');
  if (profile.diseases.includes('malaria')) risks.push('Malaria');
  if (profile.diseases.includes('zika')) risks.push('Zika virus');
  if (profile.diseases.includes('chikungunya')) risks.push('Chikungunya');
  if (profile.diseases.includes('yellow_fever')) risks.push('Yellow fever');
  if (profile.diseases.includes('typhoid')) risks.push('Typhoid fever');
  if (profile.diseases.includes('japanese_encephalitis')) risks.push('Japanese Encephalitis');
  if (profile.diseases.includes('tick_borne_encephalitis')) risks.push('Tick-borne diseases');
  if (profile.diseases.includes('meningitis')) risks.push('Meningococcal meningitis');
  if (profile.diseases.includes('heat_illness')) risks.push('Heat exhaustion and heat stroke');
  if (profile.diseases.includes('altitude_sickness')) risks.push('Altitude sickness');
  if (profile.diseases.includes('mers_cov')) risks.push('MERS-CoV (rare)');
  if (profile.diseases.includes('west_nile_virus')) risks.push('West Nile virus');
  if (profile.diseases.includes('tick_borne_illness')) risks.push('Lyme disease and tick-borne illnesses');

  // Food and water safety risks
  if (['high_risk', 'very_high_risk'].includes(profile.foodSafety)) {
    risks.push("Traveler's diarrhea");
  }
  if (['high_risk', 'very_high_risk'].includes(profile.waterSafety)) {
    risks.push('Waterborne infections');
  }

  // Climate-specific risks
  if (profile.climate === 'tropical') {
    risks.push('Heat-related illness', 'Dehydration');
  }
  if (profile.climate === 'desert') {
    risks.push('Severe dehydration', 'Heat stroke');
  }

  // Air quality risks
  if (['high_concern', 'very_high_concern'].includes(profile.airQuality)) {
    risks.push('Respiratory issues from air pollution');
  }

  // Default risks
  if (risks.length === 0) {
    risks.push('Jet lag', 'Travel fatigue');
  }

  return risks;
}

function generatePharmacyItems(profile: any, destination: string): string[] {
  const items = ['Personal medications in original packaging', 'Basic first aid kit', 'Digital thermometer'];
  
  // Determine current season for seasonal items
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const isWinter = currentMonth >= 12 || currentMonth <= 2;
  const isSpring = currentMonth >= 3 && currentMonth <= 5;
  const isSummer = currentMonth >= 6 && currentMonth <= 8;
  const isAutumn = currentMonth >= 9 && currentMonth <= 11;

  // ALWAYS include for international travel
  items.push('Melatonin 3-5mg for jet lag management');

  // Vector-borne disease prevention (seasonal considerations)
  if (profile.diseases.some((d: string) => ['dengue', 'malaria', 'zika', 'chikungunya'].includes(d))) {
    const isMosquitoSeason = profile.climate === 'tropical' && (isSummer || isAutumn);
    items.push(
      `DEET 30-50% or Picaridin 20% insect repellent${isMosquitoSeason ? ' (peak mosquito season)' : ''}`,
      'Permethrin-treated clothing',
      'Mosquito net for sleeping'
    );
  }

  // Food/water safety
  if (['moderate_risk', 'high_risk', 'very_high_risk'].includes(profile.foodSafety)) {
    items.push('Anti-diarrheal medication (Loperamide)', 'Oral rehydration salts (ORS)');
  }
  if (['moderate_risk', 'high_risk', 'very_high_risk'].includes(profile.waterSafety)) {
    items.push('Water purification tablets or portable filter', 'Electrolyte supplements');
  }

  // Climate-specific items with seasonal variations
  if (['tropical', 'desert'].includes(profile.climate)) {
    items.push(
      'Sunscreen SPF 50+ (waterproof for tropical)',
      'After-sun lotion or aloe vera gel',
      'Heat rash cream',
      'Cooling towels or cooling gel'
    );
    if (isSummer) {
      items.push('Extra electrolyte supplements for hot weather');
    }
  }
  
  if (profile.climate === 'alpine' || profile.diseases.includes('altitude_sickness')) {
    items.push(
      'Altitude sickness medication (consult doctor before travel)',
      'Lip balm with SPF for high altitude'
    );
  }
  
  // Temperate climates with seasons
  if (profile.climate === 'temperate') {
    if (isWinter) {
      items.push(
        'Cold and flu medication',
        'Vitamin D supplements (winter months)',
        'Moisturizer and lip balm for cold dry weather'
      );
    } else if (isSpring || isAutumn) {
      items.push('Antihistamines for seasonal allergies');
    }
  }

  // Air quality (seasonal considerations)
  if (['high_concern', 'very_high_concern'].includes(profile.airQuality)) {
    const isHighPollutionSeason = isWinter && ['temperate', 'varied'].includes(profile.climate);
    items.push(
      `N95 or PM2.5 masks for air pollution${isHighPollutionSeason ? ' (peak pollution season)' : ''}`,
      'Eye drops for irritation',
      'Saline nasal spray'
    );
  }

  // Tick protection (spring/summer/autumn)
  if ((isSpring || isSummer || isAutumn) && 
      (profile.diseases.includes('tick_borne_encephalitis') || profile.diseases.includes('tick_borne_illness'))) {
    items.push('Tick removal tools', 'Insect repellent for ticks (for outdoor activities)');
  }

  // Motion sickness (if coastal or mountainous)
  if (profile.climate === 'varied' || destination.toLowerCase().includes('island')) {
    items.push('Motion sickness medication (for boats/winding roads)');
  }

  // General items
  items.push(
    'Pain relievers (ibuprofen and paracetamol)',
    'Antihistamines for allergies and insect bites',
    'Antibacterial wipes and hand sanitizer',
    'Adhesive bandages and blister plasters'
  );

  return items;
}

function generateNutritionTips(profile: any, destination: string): string[] {
  const tips = ['Stay well-hydrated throughout your trip'];

  // Water safety
  if (profile.waterSafety === 'very_high_risk') {
    tips.push('Only drink bottled water from sealed bottles', 'Avoid ice cubes completely', 'Use bottled water for brushing teeth');
  } else if (['high_risk', 'moderate_risk'].includes(profile.waterSafety)) {
    tips.push('Drink bottled or boiled water', 'Avoid ice in drinks', 'Be cautious with tap water');
  } else {
    tips.push('Tap water is generally safe to drink');
  }

  // Food safety
  if (profile.foodSafety === 'very_high_risk') {
    tips.push('Eat only hot, freshly cooked food', 'Avoid raw vegetables and salads', 'Avoid street food', 'Choose fruits you can peel yourself');
  } else if (profile.foodSafety === 'high_risk') {
    tips.push('Eat freshly cooked meals while hot', 'Avoid raw or undercooked food', 'Be selective with street food');
  } else if (profile.foodSafety === 'moderate_risk') {
    tips.push('Choose reputable restaurants', 'Ensure food is properly cooked');
  } else {
    tips.push('Food safety standards are generally high', 'Enjoy local cuisine with confidence');
  }

  // Climate-specific advice
  if (['tropical', 'desert'].includes(profile.climate)) {
    tips.push('Increase fluid intake in hot weather', 'Eat light meals to avoid digestive stress');
  }

  // Altitude-specific advice
  if (profile.diseases.includes('altitude_sickness')) {
    tips.push('Avoid alcohol for first 48 hours at altitude', 'Eat carbohydrate-rich meals', 'Increase caloric intake if trekking');
  }

  return tips;
}

export async function getDestinationHealthInfo(destination: string): Promise<DestinationInfo> {
  const country = extractCountryFromDestination(destination);
  const profile = countryHealthProfiles[country];
  
  if (!profile) {
    // Default profile for unknown destinations with live vaccination data
    let vaccinations;
    try {
      vaccinations = await generateVaccinations({}, destination);
    } catch (error) {
      console.warn('Error getting vaccination data for unknown destination:', error);
      vaccinations = [
        {
          name: 'Routine Vaccines (MMR, DPT, Flu, COVID-19)',
          type: 'routine',
          timing: '2-4 weeks before travel',
          description: 'Ensure all routine vaccinations are up to date'
        },
        {
          name: 'Hepatitis A',
          type: 'recommended',
          timing: '2-4 weeks before travel',
          description: 'General travel protection against liver infection'
        },
        {
          name: 'Hepatitis B',
          type: 'recommended',
          timing: '6-8 weeks before travel (3-dose series)',
          description: 'For longer stays or higher risk activities'
        }
      ];
    }

    return {
      name: destination,
      vaccinations,
      healthRisks: ["Traveler's diarrhea", 'Jet lag', 'Dehydration'],
      pharmacyItems: ['First aid kit', 'Pain relievers', 'Anti-diarrheal medication', 'Personal medications'],
      nutritionTips: ['Stay hydrated', 'Eat regular meals', 'Try local foods gradually'],
      currentAlerts: []
    };
  }

  const vaccinations = await generateVaccinations(profile, destination);

  return {
    name: destination,
    vaccinations,
    healthRisks: generateHealthRisks(profile, destination),
    pharmacyItems: generatePharmacyItems(profile, destination),
    nutritionTips: generateNutritionTips(profile, destination),
    currentAlerts: []
  };
}